import React, { useState, useEffect } from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import { Pagination, Table } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import TagManager from 'react-gtm-module'
import { useAuth } from '../../auth'
import { toast } from 'react-toastify'
import { getReceivedInvites } from '../core/_requests'
// import {useTimeZone} from '../../../hooks/useTimeZone'
import { useDateFormat } from '../../../hooks/useDateFormat'
import { DateTime } from 'luxon'
import NoDataPage from '../../widgets/components/NoDataPage'
import SkeletonRow from '../../digital-signing/components/SkeletonRow'
import { acceptReceivedInvite } from '../../auth/core/_requests'

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>
  }
  interface FilterMeta {
    itemRank: RankingInfo
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({ itemRank })
  return itemRank.passed
}

interface PendingInvitees {
  id: number
  inviteId: number
  invitedBy: string
  invitedByEmail: string
  roleName: string
  createdAt: string
  companyName: string
  inviteToken: string
}

export const ReceivedInviteesDataTable = () => {
  const { formatMessage } = useIntl()
  const [isLoading, setIsLoading] = useState(false)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [expandedYourShareholdings, setExpandedYourShareholdings] = useState<number | null>(null)
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  const [receivedInvitesList, setReceivedInvitesList] = useState<PendingInvitees[]>([]) //eslint-disable-line
  const { companyId } = useAuth()
  // const {getTimeZoneValue} = useTimeZone()
  const { getDateValue } = useDateFormat()
  const [acceptInviteLoading, setAcceptInviteLoading] = useState(false)
  const [, setSelectedInviteId] = useState<any>()

  const handleYourRowToggle = (rowIndex: number) => {
    setExpandedYourShareholdings(expandedYourShareholdings === rowIndex ? null : rowIndex)
  }

  const columns = React.useMemo<ColumnDef<PendingInvitees, any>[]>(
    () => [
      {
        accessorKey: 'companyName',
        id: 'companyName',
        header: 'Company Name',
        cell: (info) => {
          const { companyName } = info.row.original
          return (
            <div className='d-flex align-items-center gap-2'>
              <div>
                <h5 className='m-0 font-size-13 fw-normal'>{companyName}</h5>
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'invitedBy',
        id: 'invitedBy',
        header: 'Invited By',
        cell: (info) => {
          const { invitedBy } = info.row.original
          return (
            <div className='d-flex align-items-center gap-2'>
              <div>
                <h5 className='m-0 font-size-13 fw-normal'>{invitedBy}</h5>
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'email',
        id: 'email',
        header: 'Email',
        cell: (info) => {
          const { invitedByEmail } = info.row.original
          return (
            <div className='d-flex align-items-center gap-2'>
              <div>
                <h5 className='m-0 font-size-13 fw-normal'>{invitedByEmail}</h5>
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'roleName',
        id: 'roleName',
        header: 'Invited As',
        cell: (info) => {
          return (
            <div className='d-flex align-items-center justify-content-start gap-2 '>
              <div className='py-2 px-6 rounded text-primary bg-primary bg-opacity-25'>
                {formatMessage({ id: `${info.getValue()}` })}
              </div>
              <div>{ }</div>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'createdAt',
        id: 'createdAt',
        header: 'Invited On',
        cell: (info) => {
          const dateString = (info.getValue() as string)?.toString()
          return (
            <>
              {dateString
                ? getDateValue(dateString)
                  ? getDateValue(dateString)
                  : DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_MED)
                : '-'}{' '}
            </>
          )
        },
        filterFn: 'includesString',
      },

    ], []) //eslint-disable-line

  const table = useReactTable({
    data: receivedInvitesList as PendingInvitees[],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      pagination,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    globalFilterFn: 'fuzzy',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })
  const pageCount = Math.ceil(table.getPrePaginationRowModel().rows.length / pagination.pageSize)
  const currentPage = table.getState().pagination.pageIndex

  const handlePageChange = (pageIndex: number) => {
    table.setPageIndex(pageIndex)
  }
  const getReceivedInvitesList = async () => {
    try {
      setIsLoading(true)
      const {
        data: { success, data, errors },
      } = await getReceivedInvites()
      if (success) {
        setReceivedInvitesList(
          data?.receivedInvites?.map((invitee: any, index: number) => ({
            id: index,
            inviteId: invitee.inviteId,
            inviteToken: invitee.inviteToken,
            roleName: invitee?.role?.roleName,
            createdAt: invitee?.createdAt,
            invitedBy: `${invitee?.user?.firstName} ${invitee?.user?.lastName}`,
            invitedByEmail: invitee?.user?.email,
            companyName: invitee?.company?.name,
          }))
        )
      } else {
        errors.forEach((error: string) => {
          toast.error(formatMessage({ id: error }))
        })
      }
    } catch (err) {
      console.log(err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (companyId) {
      getReceivedInvitesList()
    }
  }, [companyId]) // eslint-disable-line react-hooks/exhaustive-deps

  const acceptInvite = async (invitedId: number) => {
    try {
      setAcceptInviteLoading(true)
      const {
        data: { success, errors },
      } = await acceptReceivedInvite(invitedId)
      if (success) {
        getReceivedInvitesList()
        toast.success(formatMessage({ id: 'Invite Accepted' }))
      } else {
        errors.forEach((error: string) => {
          toast.error(formatMessage({ id: error }))
        })
      }
    } catch (err) {
      console.log(err)
    } finally {
      setAcceptInviteLoading(false)
    }
  }

  return (
    <>
      <div className='d-flex flex-md-row flex-column gap-2 mb-4'>
        <div className='d-flex flex-md-row flex-column justify-content-between align-items-md-center align-items-start w-100 gap-1'>
          <p className='mb-0 fw-semibold'>{formatMessage({ id: 'Received Invites' })}</p>
          <form className='align-items-center w-100 w-md-auto d-flex border border-2  rounded'>
            <span className='svg-icon svg-icon-3 position-absolute ms-3'>
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className='mh-50px'
              >
                <rect
                  opacity='0.5'
                  x='17.0365'
                  y='15.1223'
                  width='8.15546'
                  height='2'
                  rx='1'
                  transform='rotate(45 17.0365 15.1223)'
                  fill='currentColor'
                ></rect>
                <path
                  d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                  fill='currentColor'
                ></path>
              </svg>
            </span>
            <DebouncedInput
              value={globalFilter ?? ''}
              onChange={(value) => {
                setGlobalFilter(String(value))
                if (String(value).length > 0) {
                  TagManager.dataLayer({
                    dataLayer: {
                      // event: GTMEvent.AdvisorMentorSearchSuccessful,
                      action: 'normal',
                      // metadata: {
                      //     company_id: companyId,
                      //     user_id: currentUser?.userId,
                      //     email: currentUser?.email,
                      //     search_value: value,
                      // },
                    },
                  })
                }
              }}
              className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-13 font-weight-400 height-36'
              placeholder='Search'
            />
          </form>
        </div>
      </div>
      <div className='bg-transparent rounded-top' style={{ overflowX: 'auto' }}>
        {!isLoading &&
          (receivedInvitesList?.length === 0 || table.getRowModel().rows.length === 0) ? (
          <div className='my-5 mx-6'>
            <NoDataPage width={'w-0'} bgTransparent />
          </div>
        ) : (
          <Table className='w-100 mb-0' style={{ minWidth: '600px' }}>
            <thead className='custom-thead border-bottom'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className='font-size-12 font-weight-400 table-row border-dark-mode'
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className='min-w-120px pb-6 pt-0 px-4'
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                  <th className='min-w-120px pb-6 pt-4'>{formatMessage({ id: 'Actions' })}</th>
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                <>
                  <SkeletonRow count={10} />
                </>
              ) : (
                table.getRowModel().rows.map((row, index) => (
                  <React.Fragment key={row.id}>
                    <tr
                      key={row.id}
                      onClick={() => handleYourRowToggle(index)}
                      className={`border-bottom`}
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => (
                        <>
                          {cellIndex === 2 ? (
                            <td key={cell.id} className='pt-5 px-4 border-0'>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ) :
                            cellIndex === 3 ? (
                              <td key={cell.id} className='pt-3 px-4 border-0'>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            ) : (
                              <td key={cell.id} className='pt-5 px-4 border-0'>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            )}
                        </>
                      ))}
                      <td className='pt-3 px-4' onClick={(e) => e.stopPropagation()}>
                        <button
                          className='btn border border-primary rounded bg-primary bg-opacity-10 text-primary font-size-13 py-2'
                          onClick={() => {
                            setSelectedInviteId(row.original.inviteId)
                            acceptInvite(row.original.inviteId)
                          }}
                          disabled={acceptInviteLoading}
                        >
                          {acceptInviteLoading
                            ? 'Please wait...'
                            : formatMessage({ id: 'Accept Invite' })}
                        </button>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </Table>
        )}
        {/* <CancelInviteModal isActive={isActive} setIsActive={setIsActive} deleteInvite={deleteInvite}/> */}
      </div>
      <div
        className={`w-100 justify-content-between align-items-center px-4 py-4 bg-transparent rounded-bottom mb-8 ${receivedInvitesList && receivedInvitesList?.length < 10 ? 'd-none' : 'd-md-flex '
          }`}
      >
        <div className='d-flex gap-2 w-100 align-items-center justify-content-md-start justify-content-center mb-4 mb-md-0'>
          <select
            aria-label='Default select example'
            className='p-4 py-3 w-80px border border-1 rounded-2 font-size-13 bg-transparent text-dark '
            style={{ outline: 'none' }}
            value={pagination.pageSize}
            onChange={(e) => {
              const newSize = Number(e.target.value)
              setPagination((prev) => ({
                ...prev,
                pageSize: newSize,
                pageIndex: 0, // Reset to first page when page size changes
              }))
            }}
          >
            {[10, 20, 50, 100].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>

          <span className='font-size-13 text-center text-md-start'>
            {formatMessage({
              id: `Showing ${currentPage * pagination.pageSize + 1} to ${Math.min(
                (currentPage + 1) * pagination.pageSize,
                table.getPrePaginationRowModel().rows.length
              )} of ${table.getPrePaginationRowModel().rows.length} records`,
            })}
          </span>
        </div>

        {/* <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
          <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0}>
            {'<<'}
          </Pagination.First>

          <Pagination.Prev
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            {'<'}
          </Pagination.Prev>

          {Array.from({length: pageCount}, (_, i) => (
            <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
              {i + 1}
            </Pagination.Item>
          ))}

          <Pagination.Next
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= pageCount - 1}
          >
            {'>'}
          </Pagination.Next>

          <Pagination.Last
            onClick={() => handlePageChange(pageCount - 1)}
            disabled={currentPage >= pageCount - 1}
          >
            {'>>'}
          </Pagination.Last>
        </Pagination> */}
        <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
          {/* First Page Button */}
          <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0}>
            {'<<'}
          </Pagination.First>

          {/* Previous Page Button */}
          <Pagination.Prev
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            {'<'}
          </Pagination.Prev>

          {/* Dynamic Page Number Buttons */}
          {Array.from({ length: pageCount }, (_, i) => (
            <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
              {i + 1} {/* Display pages as 1-based index */}
            </Pagination.Item>
          ))}

          {/* Next Page Button */}
          <Pagination.Next
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= pageCount - 1}
          >
            {'>'}
          </Pagination.Next>

          {/* Last Page Button */}
          <Pagination.Last
            onClick={() => handlePageChange(pageCount - 1)}
            disabled={currentPage >= pageCount - 1}
          >
            {'>>'}
          </Pagination.Last>
        </Pagination>
      </div>
    </>
  )
}
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value, debounce, onChange])
  return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
}
